import { IsInt, IsOptional, IsString, MaxLength, Min, MinLength } from 'class-validator';
import type { ReplyCategoryMeaning } from '../../constants/campaign-outcome-meanings';

/** PD-17624 — the Inbox: campaign replies by Workd's category. */

/** What the reader made of a reply (the model's answer, checked). */
export interface CampaignReplyReadingDto {
	meaning: ReplyCategoryMeaning;
	certainty: 'sure' | 'fairly_sure' | 'unsure';
	reason: string | null;
	/** An out of office's return day, YYYY-MM-DD. */
	returnDate: string | null;
	/** The person a referral points to. */
	referredName: string | null;
	referredEmail: string | null;
}

/** Where the category on a reply came from. */
export type CampaignReplyCategorySource = 'reader' | 'instantly' | 'person';

/**
 * What Workd keeps on an inbound campaign_message's `metadata` (beside the
 * event's own fields): the reading, who set the category, the draft answer,
 * and what Workd did that the member row alone cannot show.
 */
export interface CampaignReplyHandlingModel {
	reading?: CampaignReplyReadingDto | null;
	categorySource?: CampaignReplyCategorySource | null;
	draft?: string | null;
	done?: Array<string>;
	/** The "book the meeting" task, so a second reply does not make another. */
	meetingEventId?: number | null;
}

/** One category down the left of the Inbox. */
export interface CampaignInboxCategoryDto {
	id: number;
	name: string;
	meaning: ReplyCategoryMeaning | null;
	count: number;
}

/** One person in the middle of the Inbox: their latest reply. */
export interface CampaignInboxRowDto {
	memberId: number;
	campaignId: number;
	campaignName: string | null;
	name: string | null;
	accountName: string | null;
	categoryId: number | null;
	categoryName: string | null;
	meaning: ReplyCategoryMeaning | null;
	/** email or linkedin; null when nothing was received (a bounce). */
	channel: string | null;
	preview: string | null;
	at: string | null;
}

export interface CampaignInboxPageDto {
	rows: Array<CampaignInboxRowDto>;
	total: number;
	/** Everyone under the current filters, whatever the category. */
	allCount: number;
	categories: Array<CampaignInboxCategoryDto>;
	/** Campaigns with replies, for the campaign filter. */
	campaigns: Array<{ id: number; name: string }>;
}

/** One message of a conversation, oldest first. */
export interface CampaignInboxMessageDto {
	id: number;
	direction: string;
	channel: string;
	/** "Email A", "Reply", "LinkedIn". */
	label: string;
	fromName: string | null;
	at: string | null;
	text: string | null;
}

/** The right side of the Inbox. */
export interface CampaignInboxConversationDto {
	memberId: number;
	campaignId: number;
	campaignName: string | null;
	contactId: number | null;
	clientId: number | null;
	name: string | null;
	title: string | null;
	accountName: string | null;
	place: string | null;
	email: string | null;
	phone: string | null;
	/** "Step 3 of 6", or how the run ended. */
	stepText: string | null;
	/**
	 * The rep can book a real meeting from here. Without booking pages (not every tenant
	 * with campaigns has them, and a rep may have no booking type) they say a meeting is
	 * booked instead.
	 */
	canBookMeetings: boolean;
	/** The campaign's show has a live booth, so answers can carry `{{meeting.bookingLink}}`. */
	canInsertBoothLink: boolean;
	repName: string | null;
	categoryId: number | null;
	categoryName: string | null;
	meaning: ReplyCategoryMeaning | null;
	/** Why this category: the reader's reason with how sure it was, or who picked it. */
	categoryNote: string | null;
	/** What Workd already did, oldest first. */
	done: Array<string>;
	/** A person the reply points to, to add. */
	referred: { name: string | null; email: string | null } | null;
	messages: Array<CampaignInboxMessageDto>;
	/** The mailbox a reply goes out from. */
	mailbox: string | null;
	canReply: boolean;
	/** Why an answer cannot go, when canReply is false. */
	cannotReplyReason: string | null;
	draft: string | null;
	/** Whether to write a draft when this conversation opens. */
	wantsDraft: boolean;
}

export class SetCampaignReplyCategoryDto {
	@IsInt()
	@Min(1)
	public categoryId!: number;
}

export class SendCampaignReplyDto {
	@IsString()
	@MinLength(1)
	@MaxLength(5000)
	public text!: string;
}

export class WriteCampaignReplyDraftDto {
	/** A fresh draft even when one is saved (Rewrite). */
	@IsOptional()
	public rewrite?: boolean;
}
